// TodoStatus enum
export type TodoStatus = "PENDING" | "IN_PROGRESS" | "WAITING" | "COMPLETED";

// TodoStatus 표시 이름
export const TODO_STATUS_LABELS: Record<TodoStatus, string> = {
  PENDING: "작업전",
  WAITING: "대기중",
  IN_PROGRESS: "작업중",
  COMPLETED: "작업완료",
};

// TodoDependencyType enum
export type TodoDependencyType = "BLOCKING" | "WARNING" | "NOTIFICATION";

// TodoDependencyType 표시 이름
export const TODO_DEPENDENCY_TYPE_LABELS: Record<TodoDependencyType, string> = {
  BLOCKING: "차단",
  WARNING: "경고",
  NOTIFICATION: "알림",
};

// OrderPriority enum
export type OrderPriority = "LOWEST" | "LOW" | "MEDIUM" | "HIGH" | "HIGHEST";

// OrderPriority 표시 이름
export const ORDER_PRIORITY_LABELS: Record<OrderPriority, string> = {
  LOWEST: "최저",
  LOW: "낮음",
  MEDIUM: "보통",
  HIGH: "높음",
  HIGHEST: "최고",
};

// ========== 템플릿 관련 인터페이스 ==========

// 담당자 정보
export interface TodoAssignee {
  id: number;
  userId: number;
  userName: string;
  userEmail: string;
  role: "COMPANY_ADMIN" | "COMPANY_MANAGER";
}

// 의존성 정보
export interface TodoDependency {
  id: number;
  dependsOnTemplateId: number;
  dependsOnTemplateName: string;
  dependencyType: TodoDependencyType;
}

// 연결된 속성 정보
export interface TodoLinkedAttribute {
  id: number;
  attributeId: number;
  attributeName: string;
  valueIds?: number[];
}

// Todo 템플릿
export interface TodoTemplate {
  id: number;
  productId: number;
  name: string;
  description?: string | null;
  estimatedMinutes: number;
  displayOrder: number;
  isActive: boolean;
  defaultAssignees: TodoAssignee[];
  dependencies: TodoDependency[];
  linkedAttributes: TodoLinkedAttribute[];
  createdAt: string;
  updatedAt?: string | null;
}

// 템플릿 생성 요청
export interface CreateTodoTemplateRequest {
  name: string;
  description?: string | null;
  estimatedMinutes: number;
  isActive?: boolean;
  defaultAssigneeUserIds?: number[];
  dependencies?: {
    dependsOnTemplateId: number;
    dependencyType: TodoDependencyType;
  }[];
  linkedAttributeIds?: number[];
}

// 템플릿 수정 요청
export interface UpdateTodoTemplateRequest {
  name?: string;
  description?: string | null;
  estimatedMinutes?: number;
  isActive?: boolean;
  defaultAssigneeUserIds?: number[];
  dependencies?: {
    dependsOnTemplateId: number;
    dependencyType: TodoDependencyType;
  }[];
  linkedAttributeIds?: number[];
}

// 템플릿 순서 변경 요청
export interface ReorderTodoTemplatesRequest {
  templateIds: number[];
}

// ========== 주문 To-Do 관련 인터페이스 ==========

// 메모(노트) 정보
export interface TodoNote {
  id: number;
  todoId: number;
  authorId: number;
  authorName: string;
  content: string;
  createdAt: string;
}

// 주문 To-Do 의존성 정보
export interface OrderTodoDependency {
  id: number;
  dependsOnTodoId: number;
  dependsOnTodoName: string;
  dependsOnTodoStatus: TodoStatus;
  dependencyType: TodoDependencyType;
}

// 주문 정보 (간략)
export interface OrderInfo {
  id: number;
  orderNumber: string;
  name: string;
  priority: OrderPriority;
  dueDate?: string | null;
}

// 주문 To-Do
export interface OrderTodo {
  id: number;
  orderId: number;
  templateId: number;
  name: string;
  description?: string | null;
  status: TodoStatus;
  estimatedMinutes: number;
  assignees: TodoAssignee[];
  dependencies: OrderTodoDependency[];
  notes: TodoNote[];
  order?: OrderInfo;
  startedAt?: string | null;
  completedAt?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

// To-Do 상태 변경 요청
export interface UpdateTodoStatusRequest {
  status: TodoStatus;
}

// To-Do 담당자 변경 요청
export interface UpdateTodoAssigneesRequest {
  assigneeUserIds: number[];
}

// To-Do 메모 추가 요청
export interface AddTodoNoteRequest {
  content: string;
}

// 회사 전체 To-Do 필터
export interface CompanyTodosFilter {
  status?: TodoStatus;
  assigneeUserId?: number;
  productId?: number;
  priority?: OrderPriority;
  dueDateFrom?: string;
  dueDateTo?: string;
}

// ========== API 응답 타입 ==========

export type GetTodoTemplatesResponse = TodoTemplate[];
export type GetOrderTodosResponse = OrderTodo[];
export type GetCompanyTodosResponse = OrderTodo[];
